import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Users, Store, CalendarCheck2, Trash2, Plus, Copy, Check, Shield, Power, Bug, Wrench, Inbox } from "lucide-react";
import ChangePasswordCard from "../components/ChangePasswordCard";
import AdminMaintenancePanel from "../components/AdminMaintenancePanel";
import LeadsAdminPanel from "../components/LeadsAdminPanel";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [biz, setBiz] = useState([]);
  const [tab, setTab] = useState("create");

  async function load() {
    const [s, u, b] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/businesses"),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setBiz(b.data);
  }
  useEffect(() => { load(); }, []);

  async function delUser(id) {
    if (!window.confirm("Eliminare utente?")) return;
    await api.delete(`/admin/users/${id}`);
    load();
  }

  async function delBiz(id) {
    if (!window.confirm("Eliminare attività e tutte le sue prenotazioni?")) return;
    await api.delete(`/admin/businesses/${id}`);
    load();
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-400">Admin</div>
      <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Pannello di controllo</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <StatCard icon={Users} label="Utenti" value={stats?.users ?? "—"} />
        <StatCard icon={Store} label="Attività" value={stats?.businesses ?? "—"} />
        <StatCard icon={CalendarCheck2} label="Prenotazioni" value={stats?.bookings ?? "—"} />
        <StatCard icon={CalendarCheck2} label="Confermate" value={stats?.confirmed ?? "—"} />
      </div>

      <div className="flex gap-2 mt-8 mb-4 flex-wrap">
        <button onClick={() => setTab("leads")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "leads" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-leads">
          <Inbox className="h-4 w-4" /> Lead
        </button>
        <button onClick={() => setTab("create")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "create" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-create">
          <Plus className="h-4 w-4" /> Crea attività
        </button>
        <button onClick={() => setTab("users")} className={`h-10 px-4 rounded-full text-sm font-medium ${tab === "users" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-users">Utenti</button>
        <button onClick={() => setTab("biz")} className={`h-10 px-4 rounded-full text-sm font-medium ${tab === "biz" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-biz">Attività</button>
        <button onClick={() => setTab("audit")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "audit" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-audit">
          <Shield className="h-4 w-4" /> Audit log
        </button>
        <button onClick={() => setTab("legal")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "legal" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-legal">
          Legali
        </button>
        <button onClick={() => setTab("consents")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "consents" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-consents">
          Consensi
        </button>
        <button onClick={() => setTab("bugs")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "bugs" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-bugs">
          <Bug className="h-4 w-4" /> Bug reports
        </button>
        <button onClick={() => setTab("maintenance")} className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 ${tab === "maintenance" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700"}`} data-testid="admin-tab-maintenance">
          <Wrench className="h-4 w-4" /> Status
        </button>
      </div>

      {tab === "create" && <CreateBusinessUser onCreated={load} />}
      {tab === "leads" && <LeadsAdminPanel />}
      {tab === "audit" && <AuditLog />}
      {tab === "legal" && <LegalEditor />}
      {tab === "consents" && <ConsentsRegister />}
      {tab === "bugs" && <BugReportsAdmin />}
      {tab === "maintenance" && <AdminMaintenancePanel />}

      {tab === "users" && (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              <tr><th className="text-left p-4">Nome</th><th className="text-left p-4">Email</th><th className="text-left p-4">Ruolo</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-zinc-100 dark:border-zinc-800" data-testid={`admin-user-${u.id}`}>
                  <td className="p-4 font-medium">{u.name}</td>
                  <td className="p-4 text-zinc-600 dark:text-zinc-400">{u.email}</td>
                  <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800">{u.role}</span></td>
                  <td className="p-4 text-right">
                    {u.role !== "admin" && (
                      <button onClick={() => delUser(u.id)} className="h-9 w-9 rounded-full hover:bg-red-50 hover:text-red-600 inline-flex items-center justify-center text-zinc-400 dark:text-zinc-500 dark:bg-red-950/40" data-testid={`admin-del-user-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) }
      {tab === "biz" && (
        <div className="card-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              <tr><th className="text-left p-4">Nome</th><th className="text-left p-4">Categoria</th><th className="text-left p-4">Città</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {biz.map((b) => (
                <tr key={b.id} className={`border-t border-zinc-100 ${b.suspended ? "bg-amber-50/40 dark:bg-amber-950/40" : ""}`} data-testid={`admin-biz-${b.id}`}>
                  <td className="p-4 font-medium">
                    <div className="flex items-center gap-2">
                      <span>{b.name}</span>
                      {b.suspended && <span className="text-[10px] font-semibold tracking-widest uppercase text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full dark:text-amber-300 dark:bg-amber-950/40">Sospesa</span>}
                    </div>
                  </td>
                  <td className="p-4 text-zinc-600 dark:text-zinc-400">{b.category}</td>
                  <td className="p-4 text-zinc-600 dark:text-zinc-400">{b.city}</td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => toggleSuspend(b)}
                        className={`h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition ${b.suspended ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:bg-emerald-900/40" : "bg-zinc-100 text-zinc-700 hover:bg-amber-100 hover:text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"}`}
                        data-testid={`admin-suspend-${b.id}`}
                      >
                        <Power className="h-3.5 w-3.5" />
                        {b.suspended ? "Riattiva" : "Sospendi"}
                      </button>
                      <button onClick={() => delBiz(b.id)} className="h-9 w-9 rounded-full hover:bg-red-50 hover:text-red-600 inline-flex items-center justify-center text-zinc-400 dark:text-zinc-500 dark:bg-red-950/40" data-testid={`admin-del-biz-${b.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-12 pt-8 border-t border-zinc-100 dark:border-zinc-800" data-testid="admin-security-section">
        <h2 className="font-display text-xl font-medium">Sicurezza</h2>
        <p className="text-sm text-zinc-500 mt-1 mb-4 dark:text-zinc-400">Mantieni il tuo account admin protetto.</p>
        <div className="max-w-md">
          <ChangePasswordCard />
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center dark:bg-blue-950/40">
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">{label}</div>
      </div>
      <div className="font-display text-4xl mt-3">{value}</div>
    </div>
  );
}


function CreateBusinessUser({ onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  function randomPw() {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const pw = form.password || randomPw();
      const { data } = await api.post("/admin/users", {
        name: form.name,
        email: form.email.toLowerCase(),
        password: pw,
        phone: form.phone,
        role: "business",
      });
      setCreated({ ...data, password: pw });
      setForm({ name: "", email: "", password: "", phone: "" });
      onCreated();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  }

  async function copyCreds() {
    if (!created) return;
    const text = `Prenotoo — credenziali accesso\n\nEmail: ${created.email}\nPassword: ${created.password}\n\nAccedi su: ${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  return (
    <div className="max-w-2xl">
      <div className="card-soft p-6">
        <h3 className="font-display text-xl font-medium">Crea account attività</h3>
        <p className="text-sm text-zinc-500 mt-1 mb-5 dark:text-zinc-400">Attiva manualmente un nuovo cliente SaaS. Riceverà le credenziali per accedere alla sua dashboard. Una volta dentro, potrà configurare la sua attività.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-create-form">
          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nome referente</label>
            <input required className="input-soft mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-create-name" placeholder="Es. Mario Rossi" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Email</label>
            <input type="email" required className="input-soft mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-create-email" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Telefono (opzionale)</label>
            <input className="input-soft mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="admin-create-phone" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Password iniziale</label>
            <div className="flex gap-2 mt-2">
              <input type="text" className="input-soft flex-1" placeholder="Lascia vuoto per generare automaticamente" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-create-password" />
              <button type="button" onClick={() => setForm({ ...form, password: randomPw() })} className="btn-secondary h-14 px-4 text-xs" data-testid="admin-create-genpw">
                Genera
              </button>
            </div>
            <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Se vuoto, generiamo una password casuale sicura.</div>
          </div>
          {err && <div className="text-sm text-red-600" data-testid="admin-create-error">{err}</div>}
          <button disabled={loading} className="btn-accent w-full" data-testid="admin-create-submit">
            {loading ? "Creazione…" : "Crea account attività"}
          </button>
        </form>

        {created && (
          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl dark:bg-emerald-950/40 dark:border-emerald-800/50" data-testid="admin-create-success">
            <div className="text-xs font-semibold tracking-[0.15em] uppercase text-emerald-700 mb-2 dark:text-emerald-300">Account creato ✓</div>
            <div className="text-sm space-y-1">
              <div><span className="text-zinc-500 dark:text-zinc-400">Email:</span> <span className="font-mono">{created.email}</span></div>
              <div><span className="text-zinc-500 dark:text-zinc-400">Password:</span> <span className="font-mono bg-white px-2 py-0.5 rounded dark:bg-zinc-900">{created.password}</span></div>
            </div>
            <button onClick={copyCreds} className="btn-primary h-10 px-4 text-xs mt-3" data-testid="admin-create-copy">
              {copied ? <><Check className="h-4 w-4 mr-2" /> Copiato</> : <><Copy className="h-4 w-4 mr-2" /> Copia credenziali</>}
            </button>
            <button onClick={() => setCreated(null)} className="text-xs text-zinc-500 ml-3 dark:text-zinc-400">Chiudi</button>
          </div>
        )}
      </div>
    </div>
  );
}

const ACTION_LABELS = {
  create_user: { label: "Account creato", color: "bg-emerald-100 text-emerald-800" },
  delete_user: { label: "Utente eliminato", color: "bg-red-100 text-red-800" },
  delete_business: { label: "Attività eliminata", color: "bg-red-100 text-red-800" },
  suspend_business: { label: "Attività sospesa", color: "bg-amber-100 text-amber-800" },
  unsuspend_business: { label: "Attività riattivata", color: "bg-emerald-100 text-emerald-800" },
};

function AuditLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/audit-log?limit=200").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;
  if (items.length === 0) return <div className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="audit-empty">Nessuna azione registrata.</div>;

  return (
    <div className="card-soft overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
          <tr>
            <th className="text-left p-4">Quando</th>
            <th className="text-left p-4">Attore</th>
            <th className="text-left p-4">Azione</th>
            <th className="text-left p-4">Target</th>
            <th className="text-left p-4">IP</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => {
            const lab = ACTION_LABELS[e.action] || { label: e.action, color: "bg-zinc-100 text-zinc-800" };
            const d = new Date(e.created_at);
            return (
              <tr key={e.id} className="border-t border-zinc-100 dark:border-zinc-800" data-testid={`audit-${e.id}`}>
                <td className="p-4 text-zinc-600 whitespace-nowrap dark:text-zinc-400">
                  <div>{d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}</div>
                  <div className="text-xs text-zinc-400 dark:text-zinc-500">{d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                </td>
                <td className="p-4">
                  <div className="font-medium">{e.actor_email}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">{e.actor_role}</div>
                </td>
                <td className="p-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${lab.color}`}>{lab.label}</span>
                </td>
                <td className="p-4 text-zinc-700 dark:text-zinc-300">{e.target_label || "—"}</td>
                <td className="p-4 font-mono text-xs text-zinc-500 dark:text-zinc-400">{e.ip}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}



function LegalEditor() {
  const DOCS = [
    { key: "privacy", label: "Privacy Policy", hint: "Visibile su /privacy" },
    { key: "cookie", label: "Cookie Policy", hint: "Visibile su /cookie-policy" },
    { key: "dpa", label: "DPA (Data Processing Agreement)", hint: "Visibile su /dpa — accettato dai business al primo accesso" },
  ];
  const [active, setActive] = useState("privacy");
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [resetting, setResetting] = useState(false);

  async function load(k) {
    const { data } = await api.get(`/legal/${k}`);
    setContent(data.content || "");
    setUpdatedAt(data.updated_at || null);
  }
  useEffect(() => { load(active); }, [active]);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/admin/legal/${active}`, { content });
      setSavedAt(new Date());
      await load(active);
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    if (!window.confirm("Ripristinare il testo predefinito? Le tue modifiche andranno perse.")) return;
    setResetting(true);
    try {
      await api.post(`/admin/legal/${active}/reset`);
      await load(active);
    } finally {
      setResetting(false);
    }
  }

  const current = DOCS.find((d) => d.key === active);

  return (
    <div className="space-y-5" data-testid="admin-legal-editor">
      <div className="flex gap-2 flex-wrap">
        {DOCS.map((d) => (
          <button
            key={d.key}
            onClick={() => setActive(d.key)}
            className={`h-9 px-4 rounded-full text-xs font-medium ${active === d.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
            data-testid={`admin-legal-tab-${d.key}`}
          >
            {d.label}
          </button>
        ))}
      </div>

      <div className="card-soft p-5">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="font-display text-lg font-medium">{current?.label}</div>
            <div className="text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">{current?.hint}</div>
            {updatedAt && <div className="text-[11px] text-zinc-400 mt-1 dark:text-zinc-500">Ultimo aggiornamento: {new Date(updatedAt).toLocaleString("it-IT")}</div>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={`/${active === "cookie" ? "cookie-policy" : active}`} target="_blank" rel="noreferrer" className="btn-secondary h-9 px-4 text-xs" data-testid={`admin-legal-preview-${active}`}>Anteprima</a>
            <button onClick={reset} disabled={resetting} className="h-9 px-4 rounded-full border border-zinc-300 text-xs hover:bg-zinc-50 dark:border-zinc-600 dark:hover:bg-zinc-800" data-testid={`admin-legal-reset-${active}`}>
              {resetting ? "Ripristino…" : "Ripristina default"}
            </button>
            <button onClick={save} disabled={saving} className="btn-accent h-9 px-4 text-xs" data-testid={`admin-legal-save-${active}`}>
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
        {savedAt && <div className="text-xs text-emerald-600 mb-2">Salvato ✓</div>}
        <textarea
          className="w-full h-[520px] rounded-2xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 p-4 text-sm font-mono leading-relaxed outline-none dark:border-zinc-700 dark:bg-zinc-800"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          data-testid={`admin-legal-textarea-${active}`}
          spellCheck={false}
        />
        <div className="text-[11px] text-zinc-500 mt-2 dark:text-zinc-400">
          Sintassi supportata: Markdown (# titoli, **grassetto**, [link](url), tabelle).
        </div>
      </div>
    </div>
  );
}

function ConsentsRegister() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [context, setContext] = useState("");
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (type) params.type = type;
      if (context) params.context = context;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/consents", { params });
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, context]);

  function downloadCsv() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/consents.csv`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `prenotoo-consensi-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      });
  }

  return (
    <div data-testid="admin-consents-register">
      <div className="flex items-start md:items-center justify-between flex-col md:flex-row gap-3 mb-4">
        <div>
          <h3 className="font-display text-xl font-medium">Registro trattamenti</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">Log delle accettazioni Privacy e DPA con IP e timestamp (art. 30 GDPR).</p>
        </div>
        <button onClick={downloadCsv} className="btn-accent h-10 px-4 text-sm" data-testid="consents-export-csv">
          Scarica CSV
        </button>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={type} onChange={(e) => setType(e.target.value)} className="input-soft h-10 px-3 text-sm w-auto" data-testid="consents-filter-type">
          <option value="">Tutti i tipi</option>
          <option value="privacy">Privacy</option>
          <option value="dpa">DPA</option>
        </select>
        <select value={context} onChange={(e) => setContext(e.target.value)} className="input-soft h-10 px-3 text-sm w-auto" data-testid="consents-filter-context">
          <option value="">Tutti i contesti</option>
          <option value="registration">Registrazione</option>
          <option value="booking">Prenotazione</option>
          <option value="guest_booking">Guest + Stripe</option>
          <option value="dpa_accept">Accettazione DPA</option>
        </select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-1 min-w-[240px]">
          <input placeholder="Cerca nome o email…" value={q} onChange={(e) => setQ(e.target.value)} className="input-soft h-10 px-3 text-sm flex-1" data-testid="consents-filter-q" />
          <button className="btn-secondary h-10 px-4 text-sm">Cerca</button>
        </form>
      </div>
      <div className="card-soft overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500 dark:text-zinc-400" data-testid="consents-empty">Nessun consenso registrato.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
              <tr>
                <th className="text-left p-3">Data/Ora</th>
                <th className="text-left p-3">Tipo</th>
                <th className="text-left p-3">Contesto</th>
                <th className="text-left p-3">Soggetto</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-zinc-100 dark:border-zinc-800" data-testid={`consent-${it.id}`}>
                  <td className="p-3 font-mono text-xs text-zinc-600 dark:text-zinc-400">{new Date(it.created_at).toLocaleString("it-IT")}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${it.type === "dpa" ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"}`}>{it.type}</span></td>
                  <td className="p-3 text-xs text-zinc-600 dark:text-zinc-400">{it.context}</td>
                  <td className="p-3">{it.subject_name || <span className="text-zinc-400 italic dark:text-zinc-500">anonimizzato</span>}</td>
                  <td className="p-3 text-zinc-600 text-xs dark:text-zinc-400">{it.subject_email || "—"}</td>
                  <td className="p-3 font-mono text-xs text-zinc-500 dark:text-zinc-400">{it.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="text-xs text-zinc-500 mt-3 dark:text-zinc-400">
        Retention: 3 anni dall'ultimo consenso. I dati verranno anonimizzati automaticamente alla cancellazione dell'account.
      </div>
    </div>
  );
}



// ---------- Admin: Bug Reports ----------
const BUG_STATUS = {
  new: { label: "Nuovo", bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500" },
  in_progress: { label: "In lavorazione", bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500" },
  resolved: { label: "Risolto", bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
  wontfix: { label: "Won't fix", bg: "bg-zinc-200", text: "text-zinc-700", dot: "bg-zinc-500" },
};

function BugReportsAdmin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [opened, setOpened] = useState(null); // selected report
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [list, st] = await Promise.all([
      api.get("/admin/bug-reports"),
      api.get("/admin/bug-reports/stats"),
    ]);
    setRows(list.data || []);
    setStats(st.data);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? rows : rows.filter((r) => r.status === filter);

  async function setStatus(id, status) {
    await api.patch(`/admin/bug-reports/${id}`, { status });
    load();
    if (opened?.id === id) {
      const fresh = await api.get(`/admin/bug-reports`);
      const found = fresh.data.find((r) => r.id === id);
      if (found) setOpened(found);
    }
  }
  async function saveNote(id, note) {
    await api.patch(`/admin/bug-reports/${id}`, { internal_note: note });
    load();
  }
  async function del(id) {
    if (!window.confirm("Eliminare questo bug report?")) return;
    await api.delete(`/admin/bug-reports/${id}`);
    setOpened(null);
    load();
  }

  if (loading) return <div className="p-8 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;

  return (
    <div data-testid="admin-bugs-panel">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {Object.keys(BUG_STATUS).map((k) => (
            <div key={k} className="card-soft p-4">
              <div className={`text-[11px] uppercase tracking-widest font-semibold inline-flex items-center gap-1.5 ${BUG_STATUS[k].text}`}>
                <span className={`h-2 w-2 rounded-full ${BUG_STATUS[k].dot}`} /> {BUG_STATUS[k].label}
              </div>
              <div className="font-display text-2xl mt-1">{stats[k] || 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar">
        {[
          { k: "all", label: `Tutti (${rows.length})` },
          { k: "new", label: `Nuovi (${rows.filter((r) => r.status === "new").length})` },
          { k: "in_progress", label: `In lavorazione (${rows.filter((r) => r.status === "in_progress").length})` },
          { k: "resolved", label: `Risolti (${rows.filter((r) => r.status === "resolved").length})` },
        ].map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            className={`h-9 px-4 rounded-full text-sm font-medium whitespace-nowrap ${filter === f.k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
            data-testid={`bugs-filter-${f.k}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card-soft p-8 text-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="bugs-empty">
          Nessun bug nello stato selezionato 🎉
        </div>
      ) : (
        <div className="card-soft divide-y divide-zinc-100 overflow-hidden dark:divide-zinc-800">
          {filtered.map((r) => {
            const s = BUG_STATUS[r.status] || BUG_STATUS.new;
            return (
              <button
                key={r.id}
                onClick={() => setOpened(r)}
                className="w-full p-4 hover:bg-zinc-50 flex items-start gap-3 text-left dark:hover:bg-zinc-800"
                data-testid={`bug-row-${r.id}`}
              >
                <span className={`mt-1 h-2 w-2 rounded-full ${s.dot} shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${s.bg} ${s.text}`}>{s.label}</span>
                    <span className="text-[11px] text-zinc-400 dark:text-zinc-500">{new Date(r.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    <span className="text-[11px] text-zinc-500 dark:text-zinc-400">· {r.user_name} ({r.user_role}){r.business_name ? ` · ${r.business_name}` : ""}</span>
                  </div>
                  <div className="font-medium mt-0.5 truncate">{r.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5 line-clamp-1 dark:text-zinc-400">{r.description}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {opened && (
        <BugDetailModal
          report={opened}
          onClose={() => setOpened(null)}
          onSetStatus={(s) => setStatus(opened.id, s)}
          onSaveNote={(n) => saveNote(opened.id, n)}
          onDelete={() => del(opened.id)}
        />
      )}
    </div>
  );
}

function BugDetailModal({ report, onClose, onSetStatus, onSaveNote, onDelete }) {
  const [note, setNote] = useState(report.internal_note || "");
  const [shot, setShot] = useState(null);
  const [loadingShot, setLoadingShot] = useState(false);

  async function loadScreenshot() {
    setLoadingShot(true);
    try {
      const { data } = await api.get(`/admin/bug-reports?status=${report.status}`);
      const full = data.find((r) => r.id === report.id);
      setShot(full?.screenshot_b64 || "");
    } finally {
      setLoadingShot(false);
    }
  }
  useEffect(() => { loadScreenshot(); /* eslint-disable-next-line */ }, [report.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={onClose} data-testid="bug-detail-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl my-auto dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-zinc-100 dark:border-zinc-800">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold dark:text-zinc-400">Bug report</div>
            <h3 className="font-display text-2xl font-medium mt-1">{report.title}</h3>
            <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">
              {report.user_name} · {report.user_email} · {report.user_role}
              {report.business_name && <> · <strong>{report.business_name}</strong></>}
              {" · "}{new Date(report.created_at).toLocaleString("it-IT")}
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700"><Check className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1 dark:text-zinc-400">Descrizione</div>
            <pre className="bg-zinc-50 rounded-xl p-3 text-sm whitespace-pre-wrap font-sans dark:bg-zinc-800">{report.description}</pre>
          </div>
          {report.url && (
            <div className="text-xs text-zinc-600 dark:text-zinc-400">
              <span className="text-zinc-400 dark:text-zinc-500">URL:</span> <a href={report.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{report.url}</a>
            </div>
          )}
          {report.user_agent && (
            <div className="text-xs text-zinc-600 break-words dark:text-zinc-400"><span className="text-zinc-400 dark:text-zinc-500">Browser:</span> {report.user_agent}</div>
          )}

          {loadingShot ? (
            <div className="text-xs text-zinc-500 dark:text-zinc-400">Caricamento screenshot…</div>
          ) : shot ? (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 dark:text-zinc-400">Screenshot</div>
              <a href={shot} target="_blank" rel="noreferrer" className="block">
                <img src={shot} alt="Screenshot bug" className="max-w-full border border-zinc-200 rounded-xl shadow-sm hover:shadow-md transition dark:border-zinc-700" />
              </a>
            </div>
          ) : null}

          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 dark:text-zinc-400">Nota interna</div>
            <textarea
              rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              className="input-soft h-auto py-3" maxLength={2000}
              placeholder="Es. riprodotto, root cause, PR #..."
              data-testid="bug-internal-note"
            />
            <button onClick={() => onSaveNote(note)} className="btn-secondary h-9 text-xs mt-2" data-testid="bug-save-note">Salva nota</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 p-6 border-t border-zinc-100 bg-zinc-50 rounded-b-3xl dark:border-zinc-800 dark:bg-zinc-800">
          {Object.keys(BUG_STATUS).map((k) => (
            <button
              key={k}
              onClick={() => onSetStatus(k)}
              disabled={report.status === k}
              className={`h-9 px-3 rounded-full text-xs font-semibold ${report.status === k ? `${BUG_STATUS[k].bg} ${BUG_STATUS[k].text}` : "bg-white border border-zinc-200 hover:border-zinc-400 text-zinc-700"}`}
              data-testid={`bug-set-${k}`}
            >
              {BUG_STATUS[k].label}
            </button>
          ))}
          <button
            onClick={onDelete}
            className="ml-auto h-9 px-3 rounded-full text-xs font-semibold bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1 dark:bg-zinc-900 dark:text-rose-300"
            data-testid="bug-delete"
          >
            <Trash2 className="h-3.5 w-3.5" /> Elimina
          </button>
        </div>
      </div>
    </div>
  );
}
